import React, { useState } from 'react';
import { C, S } from '../../utils/styles';
import Icon from '../Icon';
import UserManagement from './UserManagement';
import PlanTiersEditor from './PlanTiersEditor';
import FederalTaxEditor from './FederalTaxEditor';
import FederalConstantsEditor from './FederalConstantsEditor';
import EngineSettingsEditor from './EngineSettingsEditor';
import EmployerPlanRulesEditor from './EmployerPlanRulesEditor';
import OptimizationRulesEditor from './OptimizationRulesEditor';
import RevenueCommissionsEditor from './RevenueCommissionsEditor';
import AdvisorSettingsEditor from './AdvisorSettingsEditor';
import StateTaxEditor from './StateTaxEditor';
import ReportContentEditor from './ReportContentEditor';
import ContractManagement from './ContractManagement';
import CommunicationsEditor from './CommunicationsEditor';
import TaxApiConfig from './TaxApiConfig';
import IBOSLayersEditor from './IBOSLayersEditor';
import AutoMessagesEditor from './AutoMessagesEditor';
import CensusFormatMappingsEditor from './CensusFormatMappingsEditor';
import MinimumWageJurisdictionsEditor from './MinimumWageJurisdictionsEditor';
import LocalIncomeTaxEditor from './LocalIncomeTaxEditor';
import CoursesListEditor from './learning/CoursesListEditor';
import CertificationsAdminPanel from './learning/CertificationsAdminPanel';

const adminTabs = [
  { id: 'users', label: 'Users', icon: 'people-outline' },
  { id: 'contracts', label: 'Contracts', icon: 'document-text-outline' },
  { id: 'report', label: 'Report Content', icon: 'document-text-outline' },
  { id: 'communications', label: 'Communications', icon: 'mail-outline' },
  { 
    id: 'planManagement',
    label: 'Plan Mgmt',
    icon: 'list-outline',
    isParent: true,
    children: [
      { id: 'plans', label: 'Plan Tiers', icon: 'layers-outline' },
      { id: 'employerRules', label: 'Employer Plan Rules', icon: 'shield-checkmark-outline' },
      { id: 'optimizationRules', label: 'Optimization Rules', icon: 'options-outline' },
    ]
  },
  { id: 'revenueCommissions', label: 'Revenue & Commissions', icon: 'cash-outline' },
  { id: 'ibosLayers', label: 'IBOS & 105 Advisor', icon: 'layers-outline' },
  { id: 'learningCourses', label: 'E-Learning', icon: 'school-outline' },
  { id: 'learningCertifications', label: 'Certifications', icon: 'ribbon-outline' },
  { id: 'autoMessages', label: 'Auto-Messages', icon: 'chatbubbles-outline' },
  { id: 'censusFormats', label: 'Census Format Mappings', icon: 'cloud-upload-outline' },
  { 
    id: 'taxManagement',
    label: 'Tax Mgmt',
    icon: 'calculator-outline',
    isParent: true,
    children: [
      { id: 'federalTax', label: 'Federal Tax', icon: 'analytics-outline' },
      { id: 'constants', label: 'Federal Constants', icon: 'settings-outline' },
      { id: 'states', label: 'State Taxes', icon: 'map-outline' },
      { id: 'minWageJurisdictions', label: 'Minimum Wage', icon: 'pricetag-outline' },
      { id: 'localIncomeTax', label: 'Local Income Tax', icon: 'business-outline' },
      { id: 'taxApi', label: 'Tax Data API', icon: 'cloud-download-outline' },
    ]
  },
  { id: 'advisorSettings', label: 'Advisor Settings', icon: 'bulb-outline' },
  { id: 'engine', label: 'System Defaults', icon: 'cog-outline' },
];

export default function AdminPanel() {
  const [activeSection, setActiveSection] = useState('users');
  const [expandedMenus, setExpandedMenus] = useState({}); // Default closed

  const toggleMenu = (menuId) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }));
  };

  const handleSectionChange = (sectionId, parentId) => {
    setActiveSection(sectionId);
    // If clicking a child item, ensure parent is expanded
    if (parentId && !expandedMenus[parentId]) {
      setExpandedMenus(prev => ({ ...prev, [parentId]: true }));
    }
  };

  return (
    <div data-testid="admin-panel" style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 16, alignItems: 'start' }}>
      {/* Sidebar */}
      <div style={S.card}>
        <h3 style={S.heading}>Configuration</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {adminTabs.map(tab => (
            <div key={tab.id}>
              {/* Parent Menu Item or Regular Item */}
              <button
                data-testid={`admin-tab-${tab.id}`}
                onClick={() => tab.isParent ? toggleMenu(tab.id) : handleSectionChange(tab.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: 'none',
                  background: (tab.isParent && expandedMenus[tab.id]) ? 'rgb(248, 250, 252)' : (activeSection === tab.id ? C.accentBg : 'transparent'),
                  color: (tab.isParent && expandedMenus[tab.id]) ? C.text : (activeSection === tab.id ? C.accent : C.textMid),
                  fontSize: 13,
                  fontWeight: (tab.isParent || activeSection === tab.id) ? 600 : 500,
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.15s',
                  fontFamily: "'Inter', sans-serif",
                  width: '100%'
                }}
              >
                <Icon name={tab.icon} style={{ fontSize: '18px' }} />
                <span style={{ flex: 1 }}>{tab.label}</span>
                {tab.isParent && (
                  <Icon 
                    name={expandedMenus[tab.id] ? 'chevron-down-outline' : 'chevron-forward-outline'} 
                    style={{ fontSize: '16px' }} 
                  />
                )}
              </button>

              {/* Child Menu Items */}
              {tab.isParent && expandedMenus[tab.id] && (
                <div style={{ marginLeft: 8, marginTop: 2, marginBottom: 2 }}>
                  {tab.children.map(child => (
                    <button
                      key={child.id}
                      data-testid={`admin-tab-${child.id}`}
                      onClick={() => handleSectionChange(child.id, tab.id)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '8px 12px',
                        paddingLeft: 20,
                        borderRadius: 8,
                        border: 'none',
                        background: activeSection === child.id ? C.accentBg : 'transparent',
                        color: activeSection === child.id ? C.accent : C.textMid,
                        fontSize: 12,
                        fontWeight: activeSection === child.id ? 600 : 500,
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'all 0.15s',
                        fontFamily: "'Inter', sans-serif",
                        width: '100%',
                        position: 'relative'
                      }}
                    >
                      {/* Indent line */}
                      <div style={{
                        position: 'absolute',
                        left: 10,
                        top: 0,
                        bottom: 0,
                        width: 2,
                        background: 'rgb(226, 232, 240)',
                        borderRadius: 1
                      }} />
                      <Icon name={child.icon} style={{ fontSize: '16px' }} />
                      {child.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {activeSection === 'users' && <UserManagement />}
        {activeSection === 'contracts' && <ContractManagement />}
        {activeSection === 'communications' && <CommunicationsEditor />}
        {activeSection === 'plans' && <PlanTiersEditor />}
        {activeSection === 'employerRules' && <EmployerPlanRulesEditor />}
        {activeSection === 'optimizationRules' && <OptimizationRulesEditor />}
        {activeSection === 'revenueCommissions' && <RevenueCommissionsEditor />}
        {activeSection === 'ibosLayers' && <IBOSLayersEditor />}
        {activeSection === 'learningCourses' && <CoursesListEditor />}
        {activeSection === 'learningCertifications' && <CertificationsAdminPanel />}
        {activeSection === 'autoMessages' && <AutoMessagesEditor />}
        {activeSection === 'censusFormats' && <CensusFormatMappingsEditor />}
        {activeSection === 'federalTax' && <FederalTaxEditor />}
        {activeSection === 'constants' && <FederalConstantsEditor />}
        {activeSection === 'states' && <StateTaxEditor />}
        {activeSection === 'minWageJurisdictions' && <MinimumWageJurisdictionsEditor />}
        {activeSection === 'localIncomeTax' && <LocalIncomeTaxEditor />}
        {activeSection === 'taxApi' && <TaxApiConfig />}
        {activeSection === 'advisorSettings' && <AdvisorSettingsEditor />}
        {activeSection === 'engine' && <EngineSettingsEditor />}
        {activeSection === 'report' && <ReportContentEditor />}
      </div>
    </div>
  );
}
